import React from "react";
import { formatCurrency } from "../../utils/formatCurrency";
import { ProposalStrategyView } from "./strategy/ProposalStrategyView";
import { computeUnits, strategyFromWizard, unitCount } from "./strategy/StrategyData";
import { ANON_PRICE_TEASER, tierShortBlurb } from "./strategyCopy";
import { TIER_COPY } from "./tiers";
import { BLACK, BORDER, CARD_BG, FONT, GOLD_DARK, GOLD_TINT, GREEN, GREEN_BG, SURFACE_2, TEXT_FAINT, TEXT_MUTED } from "./theme";
import {
    ProposalExamplesResponse,
    ProposalPricingPreviewResponse,
    ProposalTier,
    SelectedChannel,
    TIER_ORDER,
    WizardObjective,
} from "./types";

interface Step3Props {
    objective: WizardObjective | undefined;
    secondaryObjective: WizardObjective | undefined;
    categoryName: string | null;
    selectedChannels: SelectedChannel[];
    tier: ProposalTier | null;
    onTierChange: (tier: ProposalTier) => void;
    preview: ProposalPricingPreviewResponse | null;
    previewLoading: boolean;
    // Full breakdowns arrive only for internal viewers — gate the pricing UI.
    showBreakdowns: boolean;
    // Dollar figures render only for authenticated viewers (TDC-1563).
    // Anonymous users get neutral tier cards (video counts, no totals or
    // savings badges) plus the ANON_PRICE_TEASER line once under the grid.
    // The API already nulls anonymous preview totals — this drives the copy.
    showPricing: boolean;
    // TDC-1591: the ANON_PRICE_TEASER contains a dollar figure ("$20k"), so
    // RMN viewers suppress it too — they get neither pricing nor the teaser.
    // Defaults true so anonymous callers keep today's behavior.
    showPriceTeaser?: boolean;
    examples?: ProposalExamplesResponse | null;
    // The analysis-drawn strategy description (suggestion or saved).
    description?: string | null;
}

const TIER_BADGES: Record<ProposalTier, string> = {
    good: "1",
    better: "2",
    best: "3",
};

// Compact good/better/best selector cards on top; the selected package
// expands into the full slide-3 strategy layout (selections rail, numbered
// video mix, results stats, why-this-mix rail) below.
export function Step3Packages({
    objective,
    secondaryObjective,
    categoryName,
    selectedChannels,
    tier,
    onTierChange,
    preview,
    previewLoading,
    showBreakdowns,
    showPricing,
    showPriceTeaser = true,
    examples,
    description,
}: Step3Props) {
    const hasSecondary = !!secondaryObjective;
    const noChannelsCopy = showPricing
        ? "Select channels to see pricing"
        : "Select channels to see your mix";

    return (
        <div>
            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BLACK, margin: "0 0 6px" }}>
                Here's your recommended video strategy
            </h2>
            <p style={{ fontFamily: FONT, fontSize: 14, color: TEXT_MUTED, margin: "0 0 20px", lineHeight: 1.6 }}>
                Based on your goals and placements, we've created a custom video mix designed to deliver the
                biggest impact. Pick the package that fits.
            </p>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                    gap: 12,
                    alignItems: "stretch",
                    marginBottom: 22,
                }}
            >
                {TIER_ORDER.map((t) => {
                    const estimate = preview ? preview.tiers[t] : undefined;
                    const selected = t === tier;
                    const units = computeUnits(objective, secondaryObjective, t);
                    const videoCount = estimate ? estimate.projectCount : unitCount(units);
                    return (
                        <button
                            key={t}
                            type="button"
                            onClick={() => onTierChange(t)}
                            style={{
                                fontFamily: FONT,
                                textAlign: "left",
                                display: "flex",
                                flexDirection: "column",
                                background: selected ? GOLD_TINT : CARD_BG,
                                border: `1.5px solid ${selected ? GOLD_DARK : BORDER}`,
                                borderRadius: 14,
                                padding: "16px 15px",
                                cursor: "pointer",
                                transition: "border-color .15s, background .15s",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span
                                    style={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: "50%",
                                        background: selected ? GOLD_DARK : SURFACE_2,
                                        color: selected ? "#fff" : TEXT_MUTED,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {TIER_BADGES[t]}
                                </span>
                                <span style={{ fontSize: 16, fontWeight: 700, color: BLACK }}>
                                    {TIER_COPY[t].label}
                                </span>
                                {showPricing && estimate && estimate.savingsPct > 0 && (
                                    <span
                                        style={{
                                            marginLeft: "auto",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: GREEN,
                                            background: GREEN_BG,
                                            borderRadius: 99,
                                            padding: "3px 8px",
                                        }}
                                    >
                                        Save {Math.round(estimate.savingsPct)}%
                                    </span>
                                )}
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: TEXT_MUTED,
                                    lineHeight: 1.5,
                                    marginBottom: 12,
                                    flex: 1,
                                }}
                            >
                                {tierShortBlurb(t, hasSecondary)}
                            </div>
                            <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 10 }}>
                                {showPricing && (
                                    <>
                                        <div style={{ fontSize: 11, color: TEXT_FAINT, marginBottom: 2 }}>
                                            Estimated investment
                                        </div>
                                        <div style={{ fontSize: 19, fontWeight: 700, color: BLACK }}>
                                            {previewLoading && !estimate
                                                ? "…"
                                                : estimate && estimate.total != null
                                                    ? formatCurrency(estimate.total)
                                                    : "—"}
                                        </div>
                                    </>
                                )}
                                <div style={{ fontSize: 11.5, color: TEXT_FAINT }}>
                                    {videoCount > 0
                                        ? `${videoCount} expert videos · final quote confirmed by our team`
                                        : noChannelsCopy}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {!showPricing && showPriceTeaser && (
                <div
                    style={{
                        fontFamily: FONT,
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: GOLD_DARK,
                        textAlign: "center",
                        margin: "0 0 18px",
                    }}
                >
                    {ANON_PRICE_TEASER} — final quote confirmed by our team.
                </div>
            )}

            {tier && (
                <ProposalStrategyView
                    data={strategyFromWizard({
                        objective,
                        secondaryObjective,
                        categoryName,
                        selectedChannels,
                        tier,
                        preview,
                        description,
                    })}
                    showPricingBreakdowns={showBreakdowns}
                    perContentTypeEstimates={
                        preview && preview.tiers[tier]
                            ? preview.tiers[tier]!.per_content_type_estimates || null
                            : null
                    }
                    examples={examples}
                    hideOptionalUnits
                />
            )}
        </div>
    );
}
